import React, { useRef, useState, useCallback } from "react";
import { Upload, FileText, X, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx"];
const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return "0 KB";
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * ============================================================
 * RESUME UPLOADER COMPONENT
 * ============================================================
 * Handles client-side drag-and-drop & file picker, validates
 * MIME/extension (.pdf, .doc, .docx) and 5MB limit BEFORE upload,
 * and displays active upload progress.
 */
export default function ResumeUploader({
  file,
  onFileSelect,
  onFileRemove,
  error,
  uploading = false,
  uploadProgress = 0,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState("");

  const validateAndSelect = useCallback(
    (selectedFile) => {
      setLocalError("");
      if (!selectedFile) return;

      const fileName = selectedFile.name.toLowerCase();
      const hasValidExt = ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
      const hasValidMime =
        !selectedFile.type || ALLOWED_MIME_TYPES.includes(selectedFile.type);

      if (!hasValidExt && !hasValidMime) {
        setLocalError("Please upload a PDF, DOC, or DOCX file.");
        return;
      }

      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        setLocalError("Resume must be 5 MB or smaller.");
        return;
      }

      onFileSelect(selectedFile);
    },
    [onFileSelect]
  );

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || uploading) return;
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled || uploading) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSelect(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSelect(e.target.files[0]);
    }
  };

  const handleZoneClick = () => {
    if (disabled || uploading || file) return;
    inputRef.current?.click();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleZoneClick();
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (disabled || uploading) return;
    setLocalError("");
    if (inputRef.current) inputRef.current.value = "";
    onFileRemove?.();
  };

  const displayError = error || localError;

  return (
    <div className="space-y-2 w-full">
      {/* Hidden native input */}
      <input
        ref={inputRef}
        type="file"
        id="resume-file-input"
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={handleInputChange}
        disabled={disabled || uploading}
        className="sr-only"
        aria-label="Upload your resume file"
        aria-describedby={displayError ? "resume-error-msg" : undefined}
      />

      {!file ? (
        /* Empty Upload Drop Zone */
        <div
          role="button"
          tabIndex={disabled || uploading ? -1 : 0}
          onClick={handleZoneClick}
          onKeyDown={handleKeyDown}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          aria-label="Select or drag and drop your resume file"
          className={cn(
            "group relative flex flex-col items-center justify-center p-6 sm:p-8 rounded-2xl border-2 border-dashed transition-all duration-300 cursor-pointer text-center",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            isDragging
              ? "border-primary bg-primary/10 scale-[1.01] shadow-[0_0_25px_rgba(0,194,255,0.25)]"
              : displayError
              ? "border-destructive/60 bg-destructive/[0.04] hover:border-destructive"
              : "border-white/15 bg-white/[0.02] hover:border-primary/50 hover:bg-white/[0.04]",
            (disabled || uploading) && "opacity-50 cursor-not-allowed pointer-events-none"
          )}
        >
          {/* Animated Icon Glow */}
          <div
            className={cn(
              "w-12 h-12 rounded-2xl flex items-center justify-center mb-3.5 transition-transform duration-300 group-hover:scale-110",
              isDragging
                ? "bg-primary text-black shadow-[0_0_20px_rgba(0,194,255,0.6)]"
                : displayError
                ? "bg-destructive/15 text-destructive border border-destructive/30"
                : "bg-primary/15 text-primary border border-primary/25"
            )}
          >
            <Upload className="w-5 h-5" />
          </div>

          <div className="space-y-1 max-w-xs mx-auto">
            <p className="text-sm font-semibold text-white">
              <span className="text-primary hover:underline">Choose a file</span> or drag & drop
            </p>
            <p className="text-xs text-white/50">
              PDF, DOC, or DOCX formats accepted (up to 5 MB)
            </p>
          </div>
        </div>
      ) : (
        /* Selected File Card & Progress View */
        <div className="relative rounded-2xl border border-white/15 bg-white/[0.04] backdrop-blur-xl p-4 sm:p-5 shadow-lg space-y-3">
          <div className="flex items-center justify-between gap-3">
            {/* File icon and details */}
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/30 text-primary flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate max-w-[220px] sm:max-w-md">
                  {file.name}
                </p>
                <p className="text-xs text-white/50">{formatFileSize(file.size)}</p>
              </div>
            </div>

            {/* Remove / status action */}
            <div>
              {uploading ? (
                <div className="flex items-center gap-2 text-xs text-primary font-medium">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:inline">Uploading...</span>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemove}
                  disabled={disabled}
                  aria-label="Remove selected resume"
                  className="h-8 w-8 p-0 rounded-lg text-white/60 hover:text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Upload Progress Bar (when uploading) */}
          {uploading && (
            <div className="space-y-1.5 pt-1">
              <div className="flex items-center justify-between text-xs text-white/60">
                <span className="text-primary font-medium">Encrypting & uploading resume...</span>
                <span className="font-mono">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-1.5 bg-white/10" />
            </div>
          )}

          {/* Success indicator when done */}
          {!uploading && (
            <div className="flex items-center gap-1.5 text-[11px] text-green-400 font-medium pt-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Resume attached & validated</span>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {displayError && (
        <div
          id="resume-error-msg"
          role="alert"
          className="flex items-center gap-2 p-2.5 rounded-xl border border-destructive/30 bg-destructive/10 text-xs text-destructive font-medium"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{displayError}</span>
        </div>
      )}
    </div>
  );
}
